import { BodyText, toast } from "@/components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { Field, FieldLabel } from "@/components/ui";
import { Tooltip } from "@/components/ui/Tooltip";
import { EditableRecordButton } from "@/features/Patient/MedicalRecords/components/EditableRecordButton";
import { SubmitCancelButtons } from "@/features/Patient/MedicalRecords/components/SubmitCancelButtons";
import type { GenderIdentity, Patient } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { useUpdateGenderIdentity } from "../network/usePatientIdentity";

export const GENDER_IDENTITY_LABELS: Record<GenderIdentity, string> = {
  male: "Male",
  female: "Female",
  "transgender-male": "Transgender Male",
  "transgender-female": "Transgender Female",
  "non-binary": "Non-binary",
  other: "Other",
  "non-disclose": "Prefer not to disclose",
};

export const GENDER_IDENTITY_OPTIONS: {
  value: GenderIdentity;
  label: string;
}[] = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "transgender-male", label: "Transgender Male" },
  { value: "transgender-female", label: "Transgender Female" },
  { value: "non-binary", label: "Non-binary" },
  { value: "other", label: "Other" },
  { value: "non-disclose", label: "Prefer not to disclose" },
];

export const BIRTH_SEX_LABEL: Record<string, string> = {
  male: "Male",
  female: "Female",
  other: "Other",
};

const genderFormSchema = z.object({
  genderIdentity: z.string(),
});

type GenderFormValues = z.infer<typeof genderFormSchema>;

const getReadViewText = (patient: Patient): string => {
  if (patient.genderIdentity) {
    return GENDER_IDENTITY_LABELS[patient.genderIdentity];
  }
  if (patient.gender && BIRTH_SEX_LABEL[patient.gender]) {
    return `${BIRTH_SEX_LABEL[patient.gender]} (Birth Sex)`;
  }
  return "Not Set";
};

export const GenderEditor = ({ patient }: { patient: Patient }) => {
  const [isEditing, setIsEditing] = useState(false);

  const { mutateAsync: updateGenderIdentity, isPending } =
    useUpdateGenderIdentity(patient.id);

  const form = useForm<GenderFormValues>({
    resolver: zodResolver(genderFormSchema),
    defaultValues: {
      genderIdentity: patient.genderIdentity ?? "",
    },
  });

  const handleSubmit = async (values: GenderFormValues) => {
    try {
      await updateGenderIdentity({
        genderIdentity: (values.genderIdentity as GenderIdentity) || null,
      });
      setIsEditing(false);
    } catch {
      toast.error("Failed to update gender identity");
    }
  };

  const handleEditClick = () => {
    form.reset({ genderIdentity: patient.genderIdentity ?? "" });
    setIsEditing(true);
  };

  const showMismatchIndicator =
    !!patient.genderIdentity && patient.genderIdentity !== patient.gender;

  if (isEditing) {
    return (
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex relative flex-col gap-2 px-3 py-2 w-full"
      >
        <Controller
          control={form.control}
          name="genderIdentity"
          render={({ field }) => (
            <Field>
              <FieldLabel htmlFor="genderIdentity">Gender</FieldLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="genderIdentity">
                  <SelectValue placeholder="Select gender identity" />
                </SelectTrigger>
                <SelectContent>
                  {GENDER_IDENTITY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        />
        <SubmitCancelButtons
          isSaving={isPending}
          onCancel={() => setIsEditing(false)}
        />
      </form>
    );
  }

  return (
    <EditableRecordButton title="Gender" onClick={handleEditClick}>
      <div className="flex items-center gap-2">
        <BodyText size="sm">{getReadViewText(patient)}</BodyText>
        {showMismatchIndicator && (
          <Tooltip
            tooltipContentText="Patient's gender identity differs from their recorded birth sex."
            side="top"
          >
            <Info className="h-4 w-4 stroke-info shrink-0" />
          </Tooltip>
        )}
      </div>
    </EditableRecordButton>
  );
};
